import {useCallback} from 'react'; // Import useCallback
import {useVersionContext} from '../context/VersionContext';
import {useAuthContext} from '../context/AuthContext';
import {isEmbeddedInExtension} from '../lib/extensionEmbed';

export function useCreateAuthenticatedFetch() {
    const {setVersionMismatch, clientVersion} = useVersionContext();
    const {getAccessTokenOrThrow} = useAuthContext();

    const authenticatedFetch = useCallback(
        async (url: string, options: RequestInit = {}): Promise<Response> => {
            const headers = new Headers(options.headers);
            // Throws SessionExpiredError / AuthUnavailableError rather than
            // sending an unauthenticated request that would 401 and render a
            // scattered "Unauthorized." panel.
            const accessToken = await getAccessTokenOrThrow();
            headers.set('Authorization', `Bearer ${accessToken}`);

            // Add IANA timezone header
            headers.set(
                'IANA-TimeZoneId',
                Intl.DateTimeFormat().resolvedOptions().timeZone
            );

            // Signal to the backend that the app is embedded in the Chrome
            // extension side panel, so it can auto-activate the browser
            // interaction tool for runs.
            if (isEmbeddedInExtension()) {
                headers.set('X-Running-In-Extension', 'true');
            }

            const response = await fetch(url, {
                ...options,
                headers,
                credentials: 'include'
            });

            const serverVersion = response.headers.get('App-Version');

            if (
                serverVersion &&
                serverVersion !== clientVersion &&
                clientVersion !== '__version__'
            ) {
                setVersionMismatch(true);
            }

            return response;
        },
        [getAccessTokenOrThrow, clientVersion, setVersionMismatch]
    );

    return authenticatedFetch;
}

export function useCreateAnonymousFetch() {
    const {setVersionMismatch, clientVersion} = useVersionContext();

    const anonymousFetch = useCallback(
        async (url: string, options: RequestInit = {}): Promise<Response> => {
            const headers = new Headers(options.headers);

            // Add IANA timezone header
            headers.set(
                'IANA-TimeZoneId',
                Intl.DateTimeFormat().resolvedOptions().timeZone
            );

            const response = await fetch(url, {
                ...options,
                headers,
                credentials: 'include'
            });

            const serverVersion = response.headers.get('App-Version');

            if (
                serverVersion &&
                serverVersion !== clientVersion &&
                clientVersion !== '__version__'
            ) {
                setVersionMismatch(true);
            }

            return response;
        },
        [clientVersion, setVersionMismatch]
    );

    return anonymousFetch;
}
